<script setup lang="ts">
import { computed, inject } from "vue";
import PhoneField from "@/design/primitives/PhoneField.vue";
import TextField from "@/design/primitives/TextField.vue";
import { DISPLAY_CONTEXT, type DisplayContext } from "@/shared/contracts/ports";
import type { WidgetProps } from "./contract";

/** Every single-line input. The `type` attribute follows the field's meaning. */
const props = defineProps<WidgetProps & { controlId?: string; describedBy?: string }>();
const emit = defineEmits<{ "update:modelValue": [unknown]; blur: [] }>();

const text = computed(() =>
	props.modelValue === null || props.modelValue === undefined ? "" : String(props.modelValue),
);

/**
 * O telefone tem um controle PRÓPRIO — bandeira e DDI, como na V2.
 *
 * O `phone` validator recusa um número sem `+`, e com razão: `11987654321` é um
 * celular de São Paulo e também um número dos EUA. Mas quem digitava num
 * `type="tel"` comum não tinha por onde saber disso antes de errar, e a frase
 * que recebia — "inclua o código do país com + na frente" — pede um formato de
 * armazenamento a quem só queria anotar um telefone. Com o país escolhido numa
 * bandeira o `+55` vem costurado, e a recusa deixa de acontecer.
 */
const isPhone = computed(() => props.field.type === "phone");

/**
 * E A BANDEIRA QUE ABRE É A DO NEGÓCIO, não a do Brasil.
 *
 * `PhoneField` cai em `"BR"` quando ninguém lhe diz o país, e esta tela não lhe
 * dizia nada — então uma clínica em Chicago anotava o telefone de um paciente e
 * via `+55` a ser costurado no número. A máscara brasileira ainda pede onze
 * dígitos, portanto o número americano de dez ficava por terminar e a recusa
 * chegava sem dizer o motivo verdadeiro.
 *
 * O país vem do `DISPLAY_CONTEXT` porque `dynamic/` é dirigido por metadado e
 * não pode ler `features/workspace`; é a mesma injeção de onde já saem a moeda e
 * o fuso. Ausente continua a valer "não sei", e aí o `PhoneField` decide como
 * sempre decidiu.
 */
const FALLBACK_DISPLAY: DisplayContext = {
	locale: "pt-BR",
	currency: "BRL",
	timeZone: "America/Sao_Paulo",
};
const display = inject(
	DISPLAY_CONTEXT,
	computed(() => FALLBACK_DISPLAY),
);

/** Gives emails an @ key. Small, and always noticed. */
const inputType = computed(() => {
	if (props.field.type === "email") return "email";
	if (props.field.type === "url") return "url";
	return "text";
});

const placeholder = computed(() => (props.field.type === "url" ? "https://" : undefined));
</script>

<template>
	<!--
		No label and no message: the ENVELOPE around this widget draws both, and a
		label drawn on both sides appears twice. The widget only says it is invalid,
		so the control itself can announce it.
	-->
	<PhoneField
		v-if="isPhone"
		:id="controlId"
		:model-value="text"
		:default-country="display.country"
		:disabled="disabled"
		:invalid="Boolean(error)"
		:described-by="describedBy"
		@update:model-value="emit('update:modelValue', $event)"
		@blur="emit('blur')"
	/>
	<TextField
		v-else
		:id="controlId"
		:model-value="text"
		:type="inputType"
		:placeholder="placeholder"
		:disabled="disabled"
		:invalid="Boolean(error)"
		:described-by="describedBy"
		@update:model-value="emit('update:modelValue', $event)"
		@blur="emit('blur')"
	/>
</template>
